/**
 * move_watchdog.ts — PURE decision logic for the movement-health watchdog
 * (the join-freeze lesson): move failures used to be invisible — promise
 * rejections were swallowed and the server's stale-seq guard discards inputs
 * SILENTLY — so a dead pipe masqueraded as a frozen game. The one reliable
 * health signal is the authoritative row's inputSeq advancing; this module
 * decides when that silence (or an error streak) deserves a status-bar
 * warning, and when to announce recovery. Node-verifiable (verify:npc).
 */

export interface MoveHealth {
  sendsSinceAck: number;
  firstUnackedAtMs: number;
  rejectionStreak: number;
  lastServerSeq: number;
  warnedAtMs: number;
  warnActive: boolean;
}

/** Sends without a server ack before we call the pipe stalled. */
export const STALL_SENDS = 20;
/** …and at least this long since the first unacked send (ms). */
export const STALL_MS = 3000;
/** Consecutive reducer rejections before we call it a transport error. */
export const ERROR_STREAK = 5;
/** Minimum gap between repeated warnings (ms). */
export const WARN_EVERY_MS = 10_000;

export const freshMoveHealth = (): MoveHealth => ({
  sendsSinceAck: 0, firstUnackedAtMs: 0, rejectionStreak: 0,
  // warnedAtMs starts one full window in the past — a fresh session must be
  // able to warn immediately, not after 10 silent seconds (harness-caught).
  lastServerSeq: -1, warnedAtMs: -WARN_EVERY_MS, warnActive: false,
});

export function noteSend(h: MoveHealth, nowMs: number): void {
  if (h.sendsSinceAck === 0) h.firstUnackedAtMs = nowMs;
  h.sendsSinceAck += 1;
}

export function noteResolve(h: MoveHealth): void {
  h.rejectionStreak = 0; // resolved ≠ applied (the stale guard), but transport is up
}

export function noteRejection(h: MoveHealth): void {
  h.rejectionStreak += 1;
}

export type MoveHealthAction = 'none' | 'warn-stall' | 'warn-error' | 'recovered';

/**
 * Observe the authoritative inputSeq. An ADVANCE (or world-entry reset —
 * any change) proves the pipe alive: counters clear, and if a warning was
 * active we announce recovery. Otherwise, sustained silence under sends
 * (stall) or an error streak warns — rate-limited.
 */
export function observeAck(h: MoveHealth, serverSeq: number, nowMs: number): MoveHealthAction {
  if (serverSeq !== h.lastServerSeq) {
    h.lastServerSeq = serverSeq;
    h.sendsSinceAck = 0;
    h.rejectionStreak = 0;
    if (h.warnActive) { h.warnActive = false; return 'recovered'; }
    return 'none';
  }
  const stalled = h.sendsSinceAck >= STALL_SENDS && nowMs - h.firstUnackedAtMs > STALL_MS;
  const erroring = h.rejectionStreak >= ERROR_STREAK;
  if ((stalled || erroring) && nowMs - h.warnedAtMs > WARN_EVERY_MS) {
    h.warnedAtMs = nowMs;
    h.warnActive = true;
    return erroring ? 'warn-error' : 'warn-stall';
  }
  return 'none';
}
